import React, { useMemo, useState } from "react";

import { Models } from "api/types";
import { Checkbox } from "atoms/checkbox/Checkbox";
import { Radio } from "atoms/radio/Radio";
import { SearchField } from "atoms/search-field/SearchField";

export interface FilterSectionOptions {
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  useRadio?: boolean;
}

export interface FilterItem {
  id: string;
  label: string;
  checked: boolean;
  onChange: () => void;
  data?: any;
}

export interface FilterData {
  concerns: Models<"ConcernModel">[];
  areas: Models<"AreaResult">[];
  brands: Array<{ id: string; name: Models<"Translation"> }>;
  productTypes: Array<{ id: string; name: Models<"Translation"> }>;
  fulfillmentOptions: string[];
  hasSingleFulfillmentOption: boolean;
  sortOptions: { id: string; title: string }[];
}

export interface FilterSelections {
  selectedConcerns: string[];
  selectedAreas: string[];
  selectedBrands: string[];
  selectedInStoreAndDelivery: string[];
  selectedProductTypes: string[];
  selectedSort: string;
}

export interface FilterHandlers {
  onConcernsChange: (concerns: string[]) => void;
  onAreasChange: (areas: string[]) => void;
  onBrandsChange: (brands: string[]) => void;
  onProductTypesChange: (productTypes: string[]) => void;
  onDeliveryChange: (delivery: string[]) => void;
  onSortChange: (sort: string) => void;
}

export interface FilterSectionsProps {
  data: FilterData;
  selections: FilterSelections;
  handlers: FilterHandlers;
}

export const FilterItem: React.FC<{
  item: FilterItem;
  matchesSearch?: boolean;
  selectionType?: "radio" | "checkbox";
}> = ({ item, matchesSearch = true, selectionType = "checkbox" }) => (
  <div
    className={`transition-opacity duration-200 ${
      matchesSearch ? "opacity-100" : "opacity-40"
    }`}
  >
    {selectionType === "radio" ? (
      <Radio
        key={`${item.id}-${item.checked}`}
        checked={item.checked}
        onChange={() => item.onChange()}
        label={item.label}
        name={"filter-radio"}
        labelClassName="text-body3"
      />
    ) : (
      <Checkbox
        checked={item.checked}
        onChange={item.onChange}
        label={item.label}
        textStyle="text-body3"
      />
    )}
  </div>
);

export interface FilterContentOptions {
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectionType?: "radio" | "checkbox";
}

export const useFilterContent = <T,>(
  items: T[],
  selectedItems: string[],
  onItemsChange: (items: string[]) => void,
  getItemId: (item: T) => string,
  getItemLabel: (item: T) => string,
  options: FilterContentOptions = {}
) => {
  const {
    searchable = false,
    searchPlaceholder = "Search...",
    emptyMessage = 'No items found matching "{query}"',
    selectionType = "checkbox",
  } = options;
  const [searchQuery, setSearchQuery] = useState("");

  const { filteredItems, matchCount, hasNoMatches } = useMemo(() => {
    if (!searchQuery.trim()) {
      return {
        filteredItems: items.map((item) => ({ ...item, matchesSearch: true })),
        matchCount: items.length,
        hasNoMatches: false,
      };
    }

    const query = searchQuery.toLowerCase();
    const filtered = items.map((item) => ({
      ...item,
      matchesSearch: getItemLabel(item).toLowerCase().includes(query),
    }));

    return {
      filteredItems: filtered,
      matchCount: filtered.filter((item) => item.matchesSearch).length,
      hasNoMatches: !filtered.some((item) => item.matchesSearch),
    };
  }, [items, searchQuery, getItemLabel]);

  const handleRadioSelection = (optionId: string) => {
    onItemsChange([optionId]);
  };

  const handleCheckboxToggle = (optionId: string) => {
    const newSelected = selectedItems.includes(optionId)
      ? selectedItems.filter((id) => id !== optionId)
      : [...selectedItems, optionId];
    onItemsChange(newSelected);
  };

  const handleToggle = (optionId: string) => {
    if (selectionType === "radio") {
      handleRadioSelection(optionId);
    } else {
      handleCheckboxToggle(optionId);
    }
  };

  const filterItems: FilterItem[] = filteredItems.map((item) => ({
    id: getItemId(item),
    label: getItemLabel(item),
    checked: selectedItems.includes(getItemId(item)),
    onChange: () => handleToggle(getItemId(item)),
    data: item,
  }));

  return {
    searchQuery,
    setSearchQuery,
    matchCount,
    filterItems,
    hasNoMatches,
    searchPlaceholder,
    emptyMessage,
    gridClass: searchable
      ? "grid grid-cols-2 gap-2"
      : "flex flex-col space-y-2",
  };
};

export const createFilterSection = <T,>(
  id: string,
  title: string,
  items: T[],
  selectedItems: string[],
  onItemsChange: (items: string[]) => void,
  getItemId: (item: T) => string,
  getItemLabel: (item: T) => string,
  options: FilterSectionOptions = {}
) => ({
  id,
  title,
  items,
  selectedItems,
  onItemsChange,
  getItemId,
  getItemLabel,
  ...options,
});

export const FilterContent: React.FC<{
  section: any;
}> = ({ section }) => {
  const {
    searchQuery,
    setSearchQuery,
    filterItems,
    hasNoMatches,
    gridClass,
    searchPlaceholder,
    emptyMessage,
  } = useFilterContent(
    section.items,
    section.selectedItems,
    section.onItemsChange,
    section.getItemId,
    section.getItemLabel,
    {
      searchable: section.searchable,
      searchPlaceholder: section.searchPlaceholder,
      emptyMessage: section.emptyMessage,
      selectionType: section.useRadio ? "radio" : "checkbox",
    }
  );

  return (
    <div className="flex flex-col space-y-3">
      {section.searchable && (
        <SearchField
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={setSearchQuery}
          onIconClick={(side) => {
            if (side === "right") {
              setSearchQuery("");
            }
          }}
        />
      )}

      <div className={gridClass}>
        {filterItems
          .filter((item) =>
            searchQuery.trim() ? item.data?.matchesSearch : true
          )
          .map((item) => (
            <FilterItem
              key={item.id}
              item={item}
              matchesSearch={true}
              selectionType={section.useRadio ? "radio" : "checkbox"}
            />
          ))}
      </div>

      {hasNoMatches && (
        <div className="py-4 text-center text-body2 text-secondary">
          {emptyMessage.replace("{query}", searchQuery)}
        </div>
      )}
    </div>
  );
};

export const createFilterSections = ({
  data,
  selections,
  handlers,
}: FilterSectionsProps) => {
  const {
    concerns,
    areas,
    brands,
    productTypes,
    fulfillmentOptions,
    hasSingleFulfillmentOption,
    sortOptions,
  } = data;

  const {
    selectedConcerns,
    selectedAreas,
    selectedBrands,
    selectedInStoreAndDelivery,
    selectedProductTypes,
    selectedSort,
  } = selections;

  const {
    onConcernsChange,
    onAreasChange,
    onBrandsChange,
    onProductTypesChange,
    onDeliveryChange,
    onSortChange,
  } = handlers;
  const deliveryOptions = fulfillmentOptions.map((option) => ({
    id: option,
    title: option === "Product" ? "Pickup" : "Delivery",
  }));

  const sections: any[] = [
    createFilterSection(
      "sort",
      `Sort: ${getSortTitle(sortOptions, selectedSort)}`,
      sortOptions,
      [selectedSort],
      createSingleSelectHandler(onSortChange),
      (option) => option.id,
      (option) => option.title,
      {
        useRadio: true,
      }
    ),
  ];

  if (!hasSingleFulfillmentOption) {
    sections.push(
      createFilterSection(
        "inStoreAndDelivery",
        "In Store & Delivery",
        deliveryOptions,
        selectedInStoreAndDelivery,
        createMultiSelectHandler(onDeliveryChange),
        (option) => option.id,
        (option) => option.title
      )
    );
  }

  sections.push(
    createFilterSection(
      "productType",
      "Category",
      productTypes,
      selectedProductTypes,
      createMultiSelectHandler(onProductTypesChange),
      (productType) => productType.id,
      (productType) => productType.name.text ?? ""
    ),
    createFilterSection(
      "brand",
      "Brand",
      brands,
      selectedBrands,
      createMultiSelectHandler(onBrandsChange),
      (brand) => brand.id,
      (brand) => brand.name.text,
      {
        searchable: true,
        searchPlaceholder: "Search brands...",
        emptyMessage: 'No brands found matching "{query}"',
      }
    ),
    createFilterSection(
      "concern",
      "Concern",
      concerns,
      selectedConcerns,
      createMultiSelectHandler(onConcernsChange),
      (concern) => concern.id,
      (concern) => concern.name ?? ""
    ),
    createFilterSection(
      "area",
      "Area",
      areas,
      selectedAreas,
      createMultiSelectHandler(onAreasChange),
      (area) => area.id,
      (area) => area.name ?? ""
    )
  );

  return sections;
};

const createSingleSelectHandler =
  (onChange: (item: string) => void) =>
  ([item]: string[]) =>
    onChange(item);

const createMultiSelectHandler =
  (onChange: (items: string[]) => void) => (items: string[]) =>
    onChange(items);

const getSortTitle = (
  sortOptions: Array<{ id: string; title: string }>,
  selectedSort: string
) =>
  sortOptions.find((option) => option.id === selectedSort)?.title || "Featured";
